import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, CheckCircle, ArrowRight, Clock, FileText,
  User, Building2, MapPin, Briefcase, CreditCard,
  ChevronDown, ChevronUp, Zap, Star, AlertCircle,
  Smartphone, Monitor, Shield, Gift
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const ComoAbrirMeiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  // Documentos necessários
  const documentos = [
    { icon: User, nome: 'CPF', desc: 'Cadastro de Pessoa Física válido' },
    { icon: FileText, nome: 'RG ou CNH', desc: 'Documento de identidade com foto' },
    { icon: MapPin, nome: 'Comprovante de Endereço', desc: 'Residencial e comercial (se diferente)' },
    { icon: Smartphone, nome: 'Celular', desc: 'Para receber código de verificação' },
  ];

  // Passo a passo detalhado
  const passos = [
    {
      numero: 1,
      titulo: 'Acesse a plataforma',
      descricao: 'Entre no MeuMei (meumei.net) ou no Portal do Empreendedor (gov.br). Com o MeuMei, o processo é mais simples e você já ganha 7 dias grátis da plataforma completa!',
      tempo: '1 minuto',
      dica: 'Recomendamos usar computador para facilitar o preenchimento.'
    },
    {
      numero: 2,
      titulo: 'Crie sua conta Gov.br',
      descricao: 'Se ainda não tem, crie uma conta Gov.br com nível Prata ou Ouro. Você pode fazer isso pelo app Gov.br usando reconhecimento facial, bancos parceiros ou certificado digital.',
      tempo: '5 minutos',
      dica: 'O app Gov.br permite validar sua identidade usando a câmera do celular.'
    },
    {
      numero: 3,
      titulo: 'Preencha seus dados pessoais',
      descricao: 'Informe: nome completo, CPF, data de nascimento, título de eleitor ou última declaração de IR, endereço residencial, telefone e e-mail.',
      tempo: '3 minutos',
      dica: 'Confira se os dados estão iguais aos seus documentos oficiais.'
    },
    {
      numero: 4,
      titulo: 'Escolha sua atividade (CNAE)',
      descricao: 'Selecione a atividade principal que você vai exercer. São mais de 450 opções! Você pode escolher até 15 atividades secundárias também.',
      tempo: '2 minutos',
      dica: 'Escolha a atividade que melhor descreve o que você realmente faz.'
    },
    {
      numero: 5,
      titulo: 'Defina o nome fantasia',
      descricao: 'Escolha um nome comercial para sua empresa. A razão social será automaticamente seu nome + CPF.',
      tempo: '1 minuto',
      dica: 'O nome fantasia é como seus clientes vão conhecer seu negócio.'
    },
    {
      numero: 6,
      titulo: 'Informe o endereço comercial',
      descricao: 'Pode ser o mesmo endereço residencial ou outro local. Algumas atividades exigem local específico (ex: comércio).',
      tempo: '1 minuto',
      dica: 'Se trabalha em casa, pode usar seu endereço residencial.'
    },
    {
      numero: 7,
      titulo: 'Confirme e finalize',
      descricao: 'Revise todos os dados, aceite as declarações e termos, e clique em finalizar. Seu CNPJ é gerado na hora!',
      tempo: '2 minutos',
      dica: 'Guarde o CCMEI (Certificado de Condição de MEI) em local seguro.'
    },
  ];

  // O que fazer depois de abrir
  const depoisDeAbrir = [
    {
      icon: Building2,
      titulo: 'Abra conta bancária PJ',
      desc: 'Separe as finanças pessoais do negócio. Bancos digitais oferecem contas gratuitas.'
    },
    {
      icon: FileText,
      titulo: 'Configure emissão de NF',
      desc: 'Cadastre-se na prefeitura para emitir notas fiscais de serviço (NFS-e).'
    },
    {
      icon: CreditCard,
      titulo: 'Pague o DAS mensalmente',
      desc: 'Todo dia 20, pague a guia DAS (~R$ 75/mês). Use o MeuMei para gerar automaticamente.'
    },
    {
      icon: Shield,
      titulo: 'Organize-se com o MeuMei',
      desc: 'Controle financeiro, DAS automático, declaração anual e muito mais em um só lugar.'
    },
  ];

  // FAQ
  const faqs = [
    {
      pergunta: 'Quanto custa abrir um MEI?',
      resposta: 'A abertura do MEI é 100% GRATUITA! Você não paga nada para obter seu CNPJ. O único custo é o DAS mensal (R$ 71 a R$ 76) que começa no mês seguinte à abertura. Desconfie de sites que cobram para abrir MEI!'
    },
    {
      pergunta: 'Preciso ir em algum lugar presencialmente?',
      resposta: 'Não! Todo o processo é 100% online. Você faz tudo pelo computador ou celular, em qualquer lugar e horário. Não precisa ir a cartório, Junta Comercial ou prefeitura para abrir.'
    },
    {
      pergunta: 'Em quanto tempo meu CNPJ fica pronto?',
      resposta: 'O CNPJ é gerado IMEDIATAMENTE após finalizar o cadastro. Você já sai com seu número de CNPJ ativo e pode começar a trabalhar como MEI no mesmo dia.'
    },
    {
      pergunta: 'Posso abrir MEI pelo celular?',
      resposta: 'Sim! Você pode abrir pelo celular acessando o Portal do Empreendedor ou usando o app Gov.br. Porém, recomendamos usar computador para facilitar o preenchimento dos dados.'
    },
    {
      pergunta: 'Posso abrir MEI se estou empregado (CLT)?',
      resposta: 'Sim! Você pode ser MEI mesmo tendo emprego CLT. Não há impedimento legal. Porém, verifique se seu contrato de trabalho não tem cláusula de exclusividade ou restrição.'
    },
    {
      pergunta: 'MEI pode escolher qualquer nome fantasia?',
      resposta: 'Quase qualquer nome, desde que não seja idêntico a marcas registradas famosas e não contenha palavras proibidas. Evite usar "Ltda", "S.A." ou termos que indiquem outro tipo de empresa.'
    },
    {
      pergunta: 'Posso mudar a atividade do MEI depois?',
      resposta: 'Sim! Você pode alterar a atividade principal e secundárias a qualquer momento pelo Portal do Empreendedor, sem custo. A mudança é imediata.'
    },
    {
      pergunta: 'Preciso de alvará para funcionar?',
      resposta: 'O MEI tem dispensa de alvará na maioria das cidades e atividades. Porém, algumas atividades específicas (como alimentação) podem exigir licença sanitária. Consulte sua prefeitura.'
    }
  ];

  return (
    <div className="min-h-screen font-sans bg-white">
      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Como Abrir MEI: Passo a Passo Completo 2025",
          "description": "Guia completo para abrir seu MEI online e grátis em 2025. Passo a passo, documentos necessários e dicas.",
          "totalTime": "PT15M",
          "estimatedCost": { "@type": "MonetaryAmount", "currency": "BRL", "value": "0" },
          "step": passos.map((p, i) => ({
            "@type": "HowToStep",
            "position": i + 1,
            "name": p.titulo,
            "text": p.descricao
          }))
        })
      }} />

      <Header />

      {/* Hero */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/blog" className="hover:text-white">Blog</a>
            <span className="mx-2">›</span>
            <span className="text-white">Como Abrir MEI</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold mb-6"
            >
              <Gift className="w-4 h-4" />
              100% GRÁTIS
            </motion.span>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Como Abrir <span className="text-yellow-300">MEI</span><br />
              Passo a Passo
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed opacity-90 mb-8">
              Abra seu MEI online e grátis em menos de 15 minutos. 
              Guia completo com tudo que você precisa saber!
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Clock className="w-4 h-4" />
                ~15 minutos
              </span>
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <CreditCard className="w-4 h-4" />
                R$ 0,00
              </span>
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Monitor className="w-4 h-4" />
                100% Online
              </span>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* CTA Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-40 md:hidden">
        <motion.button
          onClick={() => navigateTo('https://app.meumei.net/register')}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-purple-500 to-violet-600 text-white font-bold rounded-xl flex items-center justify-center gap-2"
        >
          <Rocket className="w-5 h-5" />
          Abrir MEI Agora - Grátis
        </motion.button>
      </div>

      {/* Conteúdo */}
      <article className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Intro */}
          <section className="mb-16">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-600 leading-relaxed">
                <strong>Abrir um MEI</strong> é simples, rápido e totalmente gratuito. Em menos de 15 minutos 
                você terá seu <strong>CNPJ ativo</strong> e poderá começar a trabalhar formalizado. 
                Neste guia, vou te mostrar o <strong>passo a passo completo</strong> para abrir seu MEI em 2025.
              </p>
            </div>

            {/* Alerta */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-red-50 border border-red-200 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-red-800 mb-2">⚠️ Cuidado com golpes!</h3>
                  <p className="text-red-700">
                    A abertura do MEI é <strong>100% gratuita</strong>. Desconfie de sites que cobram 
                    para abrir seu MEI. O único site oficial é o <strong>gov.br</strong>. Você também 
                    pode usar plataformas confiáveis como o MeuMei que facilitam o processo.
                  </p>
                </div>
              </div>
            </motion.div>
          </section>

          {/* Documentos */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              📋 O que você vai precisar
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {documentos.map((doc, index) => (
                <motion.div
                  key={doc.nome}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl"
                >
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <doc.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{doc.nome}</h3>
                    <p className="text-gray-600 text-sm">{doc.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Passo a Passo */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
              🚀 Passo a Passo para Abrir seu MEI
            </h2>
            
            <div className="space-y-6">
              {passos.map((passo, index) => (
                <motion.div
                  key={passo.numero}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                      {passo.numero}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-800 text-lg">{passo.titulo}</h3>
                        <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                          {passo.tempo}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{passo.descricao}</p>
                      <div className="flex items-start gap-2 text-sm bg-amber-50 text-amber-700 p-3 rounded-lg">
                        <Star className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span><strong>Dica:</strong> {passo.dica}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Abrir MEI */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 bg-gradient-to-r from-purple-500 to-violet-600 rounded-2xl p-8 text-center text-white"
            >
              <h3 className="text-2xl font-bold mb-3">Pronto para abrir seu MEI?</h3>
              <p className="opacity-90 mb-6">
                Com o MeuMei você abre seu MEI facilmente e ainda ganha 7 dias grátis 
                da plataforma completa!
              </p>
              <motion.button
                onClick={() => navigateTo('https://app.meumei.net/register')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-purple-600 font-bold rounded-xl shadow-lg"
              >
                Abrir meu MEI Agora - É Grátis!
              </motion.button>
            </motion.div>
          </section>

          {/* Depois de Abrir */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              ✅ O que fazer depois de abrir o MEI
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {depoisDeAbrir.map((item, index) => (
                <motion.div
                  key={item.titulo}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-gray-50 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{item.titulo}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              ❓ Perguntas Frequentes
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="border border-gray-200 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.pergunta}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                          {faq.resposta}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 md:p-12 text-center text-white"
            >
              <h2 className="text-2xl md:text-4xl font-bold mb-4">
                Comece sua jornada empreendedora!
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                Mais de 15 milhões de brasileiros já são MEI. 
                Formalize seu negócio hoje e aproveite todos os benefícios!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  onClick={() => navigateTo('https://app.meumei.net/register')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-xl shadow-lg"
                >
                  Abrir MEI Grátis Agora
                </motion.button>
              </div>
              <p className="text-sm opacity-75 mt-4">
                ✓ 100% Grátis &nbsp; ✓ 15 minutos &nbsp; ✓ CNPJ na hora
              </p>
            </motion.div>
          </section>

        </div>
      </article>

      <div className="h-24 md:hidden" />
      <Footer />
    </div>
  );
};

export default ComoAbrirMeiPage;